import { Suspense } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';

import { getUsers } from '@helpers/users';
import { replaceJson, skipStaticBuild } from '@helpers/utils';
import { getStoriesBySlug } from '@helpers/posts';
import { mapApiToCard, mapJsonToCard } from '@helpers/userMap';

import { getSubTalentTypePaths, getSubTalentTypePage } from '@cms/subTalentType';
import Header from '@components/Header/DefaultHeader';

import Layout from '@components/Layout/Layout';
import SectionContainer from '@components/Section/Container';
import Section from '@components/Section/Section';
import AngleHero from '@components/PageComponents/Hero/AngleHero/AngleHero';
import DefaultFaq from '@components/PageComponents/FaqSection/DefaultFaq';
import LogoSection from '@components/PageComponents/LogoSection/LogoSection';
import TalentList from '@components/PageComponents/SubTalentTypeList/TalentList';
import FunnelSection from '@components/PageComponents/FunnelSection/FunnelSection';
import PrioritiesSection from '@components/PageComponents/PrioritiesSection/PrioritiesSection';

import Styles from '@components/PageComponents/SubTalentTypeList/Styles.module.scss';
import CardSection from '@components/CardSection/CardSection';
// const LogoSection = dynamic(() => import('@components/PageComponents/LogoSection/LogoSection'));

const PostSection = dynamic(() => import('@components/PageComponents/PostSection/PostSection'), {
  suspense: true,
});
const GenericCta = dynamic(() => import('@components/PageComponents/CallToAction/GenericCta'), {
  suspense: true,
});
const FaqSection = dynamic(() => import('@components/PageComponents/FaqSection/FaqSection'), {
  suspense: true,
});

const Reviews = dynamic(
  () => import('@components/PageComponents/SubTalentTypeList/Reviews/Reviews'),
  {
    ssr: false,
  }
);
const TalentReferenceBox = dynamic(
  () => import('@components/PageComponents/SubTalentTypeList/ReferenceBox/TalentReferenceBox'),
  {
    ssr: false,
  }
);

const removeNullProps = (obj) => {
  const returnObj = {};
  Object.entries(obj).forEach(([idx, v]) => {
    if (Array.isArray(v)) {
      if (v.length) returnObj[idx] = v;
    } else {
      if (!!v) returnObj[idx] = v;
    }
  });
  return returnObj;
};

const SubTalentTypePage = ({
  configShowCta,
  configShowPortfolio,
  configShowReviews,
  disabled,
  head,
  heroApplyButtonLabel,
  heroDescription,
  heroTitle,
  heroViewMoreButtonLabel,
  heroWorkWithButtonLabel,
  primarySkillTitle,
  reviews,
  talentReferenceDescription,
  talentType,
  users,
  hero,
  postData,
  latestStories,
  midCtaSection,
  talentCta,
  cta,
  analyticsData,
  footer,
  faqs,
  slug,
  subTalentType,
  sections,
  ...props
}) => {
  const updatedAnalyticsData = {
    talentType: slug,
    subTalentType: subTalentType,
    ...analyticsData,
  };
  return (
    <Layout
      header={Header}
      pageHead={head}
      footer={footer}
      faqData={faqs?.length ? faqs : DefaultFaq}
      buttonLabel={heroWorkWithButtonLabel}
      analyticsData={updatedAnalyticsData}>
      <AngleHero
        title={heroTitle}
        description={heroDescription}
        talentCtaLinkTo={`/talent/register`}
        talentCtaLabel={heroApplyButtonLabel}
        brandCtaLinkTo={`/get-started`}
        brandCtaLabel={heroWorkWithButtonLabel}
        analyticsData={updatedAnalyticsData}
      />
      <LogoSection />

      <SectionContainer type={SectionContainer.Type.LIGHTBLUE} className={Styles.sectionContainer}>
        <div className={Styles.container}>
          <div className={Styles.left}>
            <TalentList
              users={users}
              showPortfolio={configShowPortfolio}
              showReviewsSection={configShowReviews}
              analyticsData={updatedAnalyticsData}
            />

            <TalentReferenceBox
              talentType={talentType}
              description={talentReferenceDescription}
              analyticsData={analyticsData}
            />
          </div>
          <div className={Styles.right}>
            <Reviews
              reviews={reviews}
              talentCta={{
                buttonLabel: talentCta?.buttonLabel,
                buttonLinkTo: talentCta?.url,
                description: talentCta?.description,
                title: talentCta?.title,
              }}
              analyticsData={analyticsData}
            />
          </div>
        </div>
      </SectionContainer>

      <>
        {sections?.map((section, index) => {
          switch (section.type) {
            case 'BaseImage':
              return <div key={index}>{section.type}</div>;
            case 'CaseCallToAction':
              return (
                <SectionContainer key={index}>
                  <Section>
                    <TalentReferenceBox
                      talentType={talentType}
                      title={section.title}
                      description={section.description}
                      analyticsData={analyticsData}
                    />
                  </Section>
                </SectionContainer>
              );
            case 'BaseRichText':
              return <div key={index}>{section.type}</div>;
            case 'FaqSection':
              return <div key={index}>{section.type}</div>;
            case 'ListSection':
              return (
                <div key={index}>
                  {section.style === 'Funnel' ? (
                    <FunnelSection section={section} />
                  ) : (
                    <CardSection section={section} />
                  )}
                </div>
              );
            case 'TalentSection':
              return (
                <SectionContainer key={index}>
                  <Section title={section.title} titleClass={Styles.talentSectionTitle}>
                    <TalentList
                      key={index}
                      users={section.talents}
                      showPortfolio={configShowPortfolio}
                      showReviewsSection={configShowReviews}
                      analyticsData={updatedAnalyticsData}
                      className={Styles.talentSection}
                    />
                  </Section>
                </SectionContainer>
              );
            case 'SkillDirectorySection':
              return (
                <PrioritiesSection
                  key={index}
                  title={section.title}
                  subtitle=''
                  data={section.skills}
                  hasSearch={false}
                />
              );
            default:
              return <div key={index}>{section.type}</div>;
          }
        })}
      </>

      <Suspense>
        <FaqSection data={faqs?.length ? faqs : DefaultFaq} style={{ maxWidth: '1200px' }} />
      </Suspense>
      <Suspense>
        <GenericCta
          title={cta?.title}
          description={cta?.description}
          linkTo={cta?.url || `/get-started`}
          buttonLabel={cta?.buttonLabel}
          analyticsData={updatedAnalyticsData}
        />
      </Suspense>
      <Suspense>
        <PostSection postData={postData} />{' '}
      </Suspense>
    </Layout>
  );
};

SubTalentTypePage.propTypes = {
  config: PropTypes.object,
  head: PropTypes.object,
  title: PropTypes.object,
  footer: PropTypes.object,
  hero: PropTypes.object,
  posts: PropTypes.array,
  postData: PropTypes.array,
  latestStories: PropTypes.array,
  users: PropTypes.array,
  primarySkill: PropTypes.string,
  talentType: PropTypes.string,
  reviews: PropTypes.array,
  talentCta: PropTypes.object,
};

export default SubTalentTypePage;

export const getStaticProps = async ({ params, preview }) => {
  const { slug, subTalentType } = params;

  const { page } = await getSubTalentTypePage(`/${slug}/${subTalentType}`, preview);
  if (!page) {
    return {
      notFound: true,
      revalidate: 86400,
    };
  }

  // New talent user retrieval
  const talentSectionIndices = page.sections
    .map((s, i) => (s.type === 'TalentSection' ? i : ''))
    .filter(String);
  for (let i = 0; i < talentSectionIndices.length; i++) {
    let apiUsers = [];
    const users = page.sections[talentSectionIndices[i]].talents.map((t) => t.uuid);
    apiUsers = await getUsers(users);

    let finalUsers = apiUsers.map((apiUser) => {
      const mappedApiUser = apiUser ? mapApiToCard(apiUser) : {};

      return {
        ...removeNullProps(mappedApiUser),
        ...(page.primarySkillTitle && { primarySkillTitle: page.primarySkillTitle }), // override primary skill of json users and live users to the primary skill set on sub talent json source(page level/upper level)
      };
    });

    finalUsers = finalUsers.map((u) => {
      return {
        ...u,
        ...(page.configShowPortfolio === false && { portfolioItems: null }),
        skills: null,
      };
    });

    page.sections[talentSectionIndices[i]].talents = [...finalUsers];
  }
  // Old talent user retrieval
  let apiUsers = [];
  if (page.users.length) {
    const users = page.users.map((t) => t.uuid);
    apiUsers = await getUsers(users);
  }
  const updatedPage = replaceJson(page, page.users.length);
  const postData = await getStoriesBySlug(page.articles);

  let finalUsers = updatedPage.users.map((pageUser) => {
    const mappedPageUser = mapJsonToCard(pageUser);
    const apiUser = apiUsers.find(({ uuid }) => uuid === pageUser.uuid);
    if (pageUser.uuid && !apiUser) {
      console.error(`Uuid ${pageUser.uuid} not found for ${pageUser.username}`);
    }
    if (apiUser && !apiUser?.profile?.picture) {
      console.error(`Picture not found for ${apiUser.username}`);
    }
    const mappedApiUser = apiUser ? mapApiToCard(apiUser) : {};

    return {
      ...removeNullProps(mappedApiUser),
      ...(updatedPage.primarySkillTitle && { primarySkillTitle: updatedPage.primarySkillTitle }), // override primary skill of json users and live users to the primary skill set on sub talent json source(page level/upper level)
      ...removeNullProps(mappedPageUser),
    };
  });

  // Cleanup
  delete updatedPage.clients;

  finalUsers = finalUsers.map((u) => {
    return {
      ...u,
      ...(updatedPage.configShowPortfolio === false && { portfolioItems: null }),
      skills: null,
    };
  });

  return {
    props: {
      ...updatedPage,
      users: finalUsers,
      postData: postData || [],
      slug,
      subTalentType,
    },
    revalidate: 86400,
  };
};

export const getStaticPaths = async () => {
  if (skipStaticBuild) {
    // Fallback to provide a single return instead of hundreds of them
    return {
      paths: [{ params: { slug: 'designers', subTalentType: 'graphic-designers' } }],
      fallback: 'blocking',
    };
  } else {
    const data = await getSubTalentTypePaths();
    const paths = data.pages.map((page) => {
      const parts = page.slug.split('/');
      // For some reason split makes an array of 3
      return {
        params: {
          slug: parts[1],
          subTalentType: parts[2],
        },
      };
    });

    return {
      paths,
      fallback: 'blocking',
    };
  }
};
